// components/product/ReviewBit.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Reveal, StaggerContainer } from '../animations/Reveal';
import RatingStars from '../ui/RatingStars';
import LuxuryLoading from '../ui/LuxuryLoading';

import {
    FaQuoteRight,
    FaUserCircle,
    FaCheckCircle,
    FaInstagram,
    FaStar,
    FaHeart
} from 'react-icons/fa';
import '../../styles/globals.css';
import {ProductService} from "../../api/product.api";

const ReviewBit = ({ rating, reviewCount, reviews: initialReviews = [], ratingBreakdown = [], productId }) => {
    const [reviews, setReviews] = useState(initialReviews);
    const [loading, setLoading] = useState(!initialReviews.length);
    const [showAll, setShowAll] = useState(false);
    const [submittingReview, setSubmittingReview] = useState(false);

    useEffect(() => {
        if (!initialReviews.length && productId) {
            fetchReviews();
        }
    }, [productId, initialReviews]);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const reviewsData = await ProductService.getProductReviews(productId);
            setReviews(reviewsData || []);
        } catch (error) {
            console.error('Error fetching reviews:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddReview = async (reviewData) => {
        try {
            setSubmittingReview(true);
            await ProductService.addProductReview(productId, reviewData);
            // Refresh reviews after adding
            await fetchReviews();
        } catch (error) {
            console.error('Error adding review:', error);
        } finally {
            setSubmittingReview(false);
        }
    };

    const displayedReviews = showAll ? reviews : reviews.slice(0, 4);

    // Use provided ratingBreakdown or calculate from reviews
    const calculatedRatingBreakdown = ratingBreakdown.length > 0 ? ratingBreakdown : [
        { stars: 5, percentage: Math.round((reviews.filter(r => r.rating === 5).length / reviews.length) * 100) || 0, count: reviews.filter(r => r.rating === 5).length },
        { stars: 4, percentage: Math.round((reviews.filter(r => r.rating === 4).length / reviews.length) * 100) || 0, count: reviews.filter(r => r.rating === 4).length },
        { stars: 3, percentage: Math.round((reviews.filter(r => r.rating === 3).length / reviews.length) * 100) || 0, count: reviews.filter(r => r.rating === 3).length },
        { stars: 2, percentage: Math.round((reviews.filter(r => r.rating === 2).length / reviews.length) * 100) || 0, count: reviews.filter(r => r.rating === 2).length },
        { stars: 1, percentage: Math.round((reviews.filter(r => r.rating === 1).length / reviews.length) * 100) || 0, count: reviews.filter(r => r.rating === 1).length }
    ];

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LuxuryLoading size="medium" />
            </div>
        );
    }

    return (
        <div className="space-y-16">
            {/* Header */}
            <div className="text-center space-y-4">
                <Reveal>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-champagne/10 rounded-full">
                        <FaQuoteRight className="w-3 h-3 text-champagne" />
                        <span className="text-sm tracking-widest uppercase text-champagne">
                            Love Letters
                        </span>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <h2 className="text-4xl md:text-5xl font-heading font-light">
                        Loved by <span className="italic text-sage">Thousands</span> Worldwide
                    </h2>
                </Reveal>

                <Reveal delay={0.4}>
                    <p className="text-xl text-charcoal/60 max-w-2xl mx-auto">
                        Join thousands who have transformed their skin with our signature elixir
                    </p>
                </Reveal>
            </div>

            <div className="grid lg:grid-cols-3 gap-12">
                {/* Rating Overview */}
                <Reveal>
                    <div className="bg-gradient-to-br from-white to-pearl rounded-3xl p-8 shadow-subtle">
                        <div className="text-center mb-8">
                            <div className="text-5xl font-light text-champagne mb-2">{rating.toFixed(1)}</div>
                            <div className="mb-4">
                                <RatingStars rating={rating} size="lg" />
                            </div>
                            <div className="text-charcoal/60">
                                Based on {reviewCount} verified reviews
                            </div>
                        </div>

                        {/* Rating Breakdown */}
                        <div className="space-y-4">
                            {calculatedRatingBreakdown.map((breakdown, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <div className="flex items-center gap-1 w-16">
                                        <span className="text-sm text-charcoal/60">{breakdown.stars}</span>
                                        <FaStar className="w-3 h-3 text-champagne" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="h-2 bg-nude rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${breakdown.percentage}%` }}
                                                transition={{ duration: 1, delay: index * 0.1 }}
                                                className="h-full bg-gradient-to-r from-champagne to-sage"
                                            />
                                        </div>
                                    </div>
                                    <span className="text-sm text-charcoal/60 w-12 text-right">
                                        {breakdown.percentage}%
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Verified Badge */}
                        <div className="mt-8 pt-8 border-t border-nude/50">
                            <div className="flex items-center gap-3 justify-center">
                                <FaCheckCircle className="w-5 h-5 text-sage" />
                                <span className="font-medium text-charcoal">Verified Purchases Only</span>
                            </div>
                        </div>
                    </div>
                </Reveal>

                {/* Reviews Grid */}
                <div className="lg:col-span-2">
                    {reviews.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-charcoal/60">No reviews yet. Be the first to review!</p>
                        </div>
                    ) : (
                        <>
                            <StaggerContainer stagger={0.05}>
                                <div className="grid md:grid-cols-2 gap-6">
                                    {displayedReviews.map((review, index) => (
                                        <ReviewCard key={review._id || index} review={review} index={index} />
                                    ))}
                                </div>
                            </StaggerContainer>

                            {/* View All Button */}
                            {reviews.length > 4 && (
                                <Reveal delay={0.6}>
                                    <div className="text-center mt-12">
                                        <button
                                            onClick={() => setShowAll(!showAll)}
                                            className="px-8 py-3 bg-white text-charcoal border border-nude rounded-full hover:border-champagne transition-colors duration-300"
                                        >
                                            {showAll ? 'Show Less' : `View All ${reviewCount} Reviews`}
                                        </button>
                                    </div>
                                </Reveal>
                            )}
                        </>
                    )}
                </div>
            </div>

        </div>
    );
};

const ReviewCard = ({ review, index }) => {
    // Mock review data since API returns empty reviews array
    const mockReview = review || {
        _id: `mock-${index}`,
        rating: 5,
        text: "This serum transformed my skin! Visible results within a week.",
        name: "Sarah M.",
        location: "New York",
        date: new Date().toISOString(),
        verified: true,
        skinType: "Combination",
        likes: 24
    };

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 40 },
                visible: { opacity: 1, y: 0 }
            }}
            whileHover={{ y: -5 }}
            className="group relative"
        >
            <div className="h-full bg-white rounded-2xl p-6 shadow-subtle hover:shadow-lift transition-all duration-500">
                {/* Review Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-champagne/10 to-sage/10 flex items-center justify-center">
                            <FaUserCircle className="w-6 h-6 text-charcoal/40" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h4 className="font-medium text-charcoal">
                                    {mockReview.name}
                                </h4>
                                {mockReview.verified && (
                                    <FaCheckCircle className="w-4 h-4 text-sage" />
                                )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-charcoal/40">
                                {mockReview.location && (
                                    <>
                                        <span>{mockReview.location}</span>
                                        <span>•</span>
                                    </>
                                )}
                                <span>{new Date(mockReview.date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                })}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <FaHeart className="w-4 h-4 text-charcoal/20 group-hover:text-champagne transition-colors" />
                        <span className="text-sm text-charcoal/40">{mockReview.likes || 0}</span>
                    </div>
                </div>

                {/* Rating */}
                <div className="mb-4">
                    <RatingStars rating={mockReview.rating} />
                </div>

                {/* Review Text */}
                <p className="text-charcoal/70 mb-6 italic">
                    "{mockReview.text}"
                </p>

                {/* Review Meta */}
                <div className="flex items-center justify-between">
                    {mockReview.skinType && (
                        <div className="text-sm text-charcoal/40">
                            Skin type: {mockReview.skinType}
                        </div>
                    )}
                    {mockReview.images && mockReview.images.length > 0 ? (
                        <div className="flex items-center gap-2">
                            {mockReview.images.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={img}
                                    alt={`Review ${index + 1} image ${idx + 1}`}
                                    className="w-8 h-8 rounded-lg object-cover"
                                />
                            ))}
                        </div>
                    ) : (
                        <div></div>
                    )}
                </div>

                {/* Quote Icon */}
                <div className="absolute top-6 right-6 opacity-10">
                    <FaQuoteRight className="w-8 h-8 text-champagne" />
                </div>
            </div>
        </motion.div>
    );
};

export default ReviewBit;